import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;

const badge = (status) => {
  const s = (status || "").toLowerCase();
  const base = "inline-flex px-3 py-1 rounded-full text-xs font-bold capitalize ";
  if (s.includes("cancel")) return base + "bg-red-100 text-red-700";
  if (s.includes("dispute")) return base + "bg-amber-100 text-amber-700";
  if (s.includes("completed")) return base + "bg-green-100 text-green-700";
  if (s.includes("assigned")) return base + "bg-blue-100 text-blue-700";
  if (s.includes("pending")) return base + "bg-gray-200 text-gray-700";
  return base + "bg-gray-100 text-gray-700";
};

export default function JobDetailsModal({ jobId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) return;

    const run = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/admin/jobs/${jobId}`);
        setData(res.data || null);
      } catch (e) {
        console.error("❌ Job details fetch failed:", e);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [jobId]);

  const job = data?.job;
  const bids = Array.isArray(data?.bids) ? data.bids : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-xl text-gray-500 hover:text-red-600"
        >
          ✕
        </button>

        <div className="p-6">
          {loading ? (
            <p className="text-gray-600">Loading job details…</p>
          ) : !job ? (
            <p className="text-gray-600">No job data found.</p>
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-extrabold text-gray-800">
                    {job.jobTitle || "Untitled Job"}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Job ID: <span className="font-mono">{job._id}</span>
                  </p>
                </div>
                <span className={badge(job.status)}>{(job.status || "").replaceAll("_", " ")}</span>
              </div>

              {/* Top grid */}
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="font-bold text-gray-800 mb-2">Job Info</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p><strong>Service:</strong> {job.service || "—"}</p>
                    <p><strong>Budget:</strong> ${job.budget ?? 0}</p>
                    <p><strong>Scheduled:</strong> {job.scheduledAt ? new Date(job.scheduledAt).toLocaleString() : "—"}</p>
                    <p><strong>Created:</strong> {job.createdAt ? new Date(job.createdAt).toLocaleString() : "—"}</p>
                    <p><strong>Address:</strong> {job.address || job.street || "—"}</p>
                    <p><strong>City/Province:</strong> {job.city || "—"} / {job.province || "—"}</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="font-bold text-gray-800 mb-2">Customer</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>
                      <strong>Name:</strong>{" "}
                      {`${job.customerId?.name || ""} ${job.customerId?.lastName || ""}`.trim() || "—"}
                    </p>
                    <p><strong>Email:</strong> {job.customerId?.email || "—"}</p>
                    <p><strong>Phone:</strong> {job.customerId?.phone || "—"}</p>
                    <p><strong>Address:</strong> {job.customerId?.address || "—"}</p>
                    <p><strong>City:</strong> {job.customerId?.city || "—"}</p>
                    <p><strong>Province:</strong> {job.customerId?.province || "—"}</p>
                    <p><strong>Postal:</strong> {job.customerId?.postalCode || "—"}</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="font-bold text-gray-800 mb-2">Assigned Worker</div>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p><strong>Name:</strong> {job.assignedTo?.name || "—"}</p>
                    <p><strong>Email:</strong> {job.assignedTo?.email || "—"}</p>
                    <p><strong>Phone:</strong> {job.assignedTo?.phone || "—"}</p>
                  </div>

                  <div className="mt-3 border-t pt-3 text-sm text-gray-700">
                    <div className="font-bold text-gray-800 mb-2">Completion</div>
                    <p><strong>Worker marked:</strong> {job.completion?.workerMarkedAt ? new Date(job.completion.workerMarkedAt).toLocaleString() : "—"}</p>
                    <p><strong>Customer confirmed:</strong> {job.completion?.customerConfirmedAt ? new Date(job.completion.customerConfirmedAt).toLocaleString() : "—"}</p>
                  </div>
                </div>
              </div>

              {/* Bids */}
              <div className="mt-6">
                <h3 className="text-lg font-extrabold text-gray-800 mb-2">💬 Bids ({bids.length})</h3>
                {bids.length === 0 ? (
                  <p className="text-sm text-gray-500">No bids found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-xl shadow text-sm">
                      <thead className="bg-gray-200 text-gray-700">
                        <tr>
                          <th className="py-3 px-4 text-left">Worker</th>
                          <th className="py-3 px-4 text-left">Email</th>
                          <th className="py-3 px-4 text-left">Amount</th>
                          <th className="py-3 px-4 text-left">Status</th>
                          <th className="py-3 px-4 text-left">Change Request</th>
                          <th className="py-3 px-4 text-left">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bids.map((b) => (
                          <tr key={b._id} className="border-t hover:bg-gray-50">
                            <td className="py-2 px-4 font-semibold">{b.workerId?.name || "—"}</td>
                            <td className="py-2 px-4">{b.workerId?.email || "—"}</td>
                            <td className="py-2 px-4">${b.amount ?? 0}</td>
                            <td className="py-2 px-4 capitalize">{(b.status || "—").replaceAll("_", " ")}</td>
                            <td className="py-2 px-4">
                              {b.changeRequest?.status ? (
                                <div className="text-xs">
                                  <div className="font-bold capitalize">{b.changeRequest.status}</div>
                                  <div>New: ${b.changeRequest.newPrice ?? "—"}</div>
                                  <div className="text-gray-500">{b.changeRequest.message || ""}</div>
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="py-2 px-4">
                              {b.createdAt ? new Date(b.createdAt).toLocaleString() : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* History */}
              <div className="mt-6">
                <h3 className="text-lg font-extrabold text-gray-800 mb-2">📜 History</h3>
                {Array.isArray(job.history) && job.history.length > 0 ? (
                  <div className="bg-gray-50 rounded-xl p-4 text-sm">
                    <ul className="space-y-2">
                      {job.history
                        .slice()
                        .reverse()
                        .map((h, idx) => (
                          <li key={idx} className="border-b last:border-b-0 pb-2 last:pb-0">
                            <div className="font-semibold capitalize">
                              {String(h.action || "").replaceAll("_", " ")}
                            </div>
                            <div className="text-gray-600 text-xs">
                              {h.at ? new Date(h.at).toLocaleString() : "—"} • by {h.by || "—"}
                            </div>
                            {h.notes ? <div className="text-gray-700 mt-1">{h.notes}</div> : null}
                          </li>
                        ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No history found.</p>
                )}
              </div>

              {/* Dispute */}
              <div className="mt-6">
                <h3 className="text-lg font-extrabold text-gray-800 mb-2">⚖️ Dispute</h3>
                {job.dispute?.status ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                    <p><strong>Status:</strong> {job.dispute.status}</p>
                    <p><strong>Reason:</strong> {job.dispute.reason || "—"}</p>
                    <p><strong>Opened:</strong> {job.dispute.createdAt ? new Date(job.dispute.createdAt).toLocaleString() : "—"}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No dispute on this job.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
